import { BaseService } from "@services/BaseService";

interface EvaluationRunSchema {
  dataset_name: string;
  run_name?: string;
  run_description?: string;
  regenerate_outputs: boolean;
}

interface EvaluationRunResponse {
  message: string;
  dataset_name: string;
  run_name: string;
  run_description?: string;
  regenerate_outputs: boolean;
  task_id: string;
  status: string;
}

interface CsvUploadResponse {
  message: string;
  dataset_name: string;
  items_added: number;
  filename: string;
  file_type: string;
  columns_detected: string[];
  total_rows: number;
  status: string;
}

// Report evaluation schemas
interface ReportEvaluationRunSchema {
  dataset_name: string;
  run_name?: string;
  run_description?: string;
  // Note: No regenerate_outputs for report evaluation
}

interface SingleReportEvaluationSchema {
  ai_generated_pdf?: File;
  reference_pdf: File;
  report_id?: string;
  use_report_id: boolean;
}

interface EvaluationMetric {
  score: number;
  description: string;
  reason: string;
}

interface SingleReportResults {
  structural_fidelity: EvaluationMetric;
  content_completeness: EvaluationMetric;
  factual_faithfulness: EvaluationMetric;
  extraneous_content: EvaluationMetric;
  chart_accuracy: EvaluationMetric;
  narrative_quality: EvaluationMetric;
}

interface ReportEvaluationResponse {
  message: string;
  task_id: string;
  status: string;
  evaluation_type: 'langfuse_dataset' | 'single';
  // Additional fields based on evaluation type
  dataset_name?: string;
  run_name?: string;
  run_description?: string;
  report_id?: string;
  // Results for single report evaluation
  results?: SingleReportResults;
}

class EvaluationService extends BaseService {
  async runEvaluation(evalRun: EvaluationRunSchema): Promise<EvaluationRunResponse> {
    return this.post<EvaluationRunResponse>(`/evaluation/run`, evalRun) as Promise<EvaluationRunResponse>;
  }

  async uploadFile(datasetName: string, file: File): Promise<CsvUploadResponse> {
    const formData = new FormData();
    formData.append("dataset_name", datasetName);
    formData.append("file", file);

    return this.post<CsvUploadResponse>("/evaluation/examples/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }) as Promise<CsvUploadResponse>;
  }

  // Report evaluation methods
  async runReportEvaluation(evalRun: ReportEvaluationRunSchema): Promise<ReportEvaluationResponse> {
    return this.post<ReportEvaluationResponse>(`/evaluation/report/run`, evalRun) as Promise<ReportEvaluationResponse>;
  }

  async runSingleReportEvaluation(evaluation: SingleReportEvaluationSchema): Promise<ReportEvaluationResponse> {
    const formData = new FormData();
    
    if (evaluation.use_report_id) {
      formData.append("report_id", evaluation.report_id || "");
      formData.append("reference_pdf", evaluation.reference_pdf);
    } else {
      if (evaluation.ai_generated_pdf) {
        formData.append("ai_generated_pdf", evaluation.ai_generated_pdf);
      }
      formData.append("reference_pdf", evaluation.reference_pdf);
    }
    formData.append("use_report_id", evaluation.use_report_id.toString());

    return this.post<ReportEvaluationResponse>("/evaluation/report/single", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }) as Promise<ReportEvaluationResponse>;
  }

  async uploadReportFile(datasetName: string, file: File): Promise<CsvUploadResponse> {
    const formData = new FormData();
    formData.append("dataset_name", datasetName);
    formData.append("file", file);

    return this.post<CsvUploadResponse>("/evaluation/report/upload-dataset", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }) as Promise<CsvUploadResponse>;
  }

}

export const evaluationService = new EvaluationService();
export default EvaluationService;
export type { ReportEvaluationRunSchema, SingleReportEvaluationSchema, ReportEvaluationResponse, SingleReportResults, EvaluationMetric }; 